/**
 * /join — accept a department invite (docs/guardian/03 §1.3/§1.5, 2026-10-21).
 *
 * The token was moved off the URL into this tab's sessionStorage by
 * useGuardianAuth (utils/departmentJoin). Signed out: ask the user to sign in
 * or create an account with the invited email; the token survives that detour.
 * Signed in: accept once, then clear the token whatever the outcome, except a
 * transient failure (network/5xx), which can be retried.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { acceptDepartmentInvite } from '../api/guardianApi';
import { clearPendingJoinToken, getPendingJoinToken } from '../utils/departmentJoin';

/** User-facing text for the Backend's machine codes. */
export function joinErrorMessage(code) {
  switch (code) {
    case 'invite_not_found':
      return 'This invitation is not valid for your account. It may have expired, been revoked or already been used, or it was sent to a different email address.';
    case 'email_not_verified':
      return 'Verify your email address first, then open the invitation link again.';
    case 'already_member':
      return 'Your account already belongs to a department. Leave it first to join another one.';
    case 'seat_limit_reached':
      return 'All of this department\'s seats are in use. Ask your department admin to free one or add seats.';
    case 'department_plan_inactive':
      return 'This department does not have an active Guardian plan. Ask your department admin.';
    default:
      return null;
  }
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {() => void} props.onRequestSignIn - Opens sign-in / sign-up.
 * @param {() => void} props.onJoined - Re-reads /me and shows the department.
 * @param {() => void} props.onDone - Back to chat.
 */
export default function JoinDepartment({ authToken, onRequestSignIn, onJoined, onDone }) {
  const [state, setState] = useState({ status: 'idle', message: null });
  const started = useRef(false);

  const accept = useCallback(async () => {
    const token = getPendingJoinToken();
    if (!token) {
      setState({ status: 'missing', message: null });
      return;
    }
    setState({ status: 'joining', message: null });
    const res = await acceptDepartmentInvite(authToken, token);
    if (res?.success) {
      clearPendingJoinToken();
      setState({ status: 'joined', message: null });
      onJoined?.();
      return;
    }
    const known = joinErrorMessage(res?.data?.code);
    if (known) {
      // A final refusal: the same token will never work for this account.
      clearPendingJoinToken();
      setState({ status: 'refused', message: known });
      return;
    }
    setState({
      status: 'retry',
      message: 'We could not reach Guardian to accept the invitation. Try again in a moment.',
    });
  }, [authToken, onJoined]);

  useEffect(() => {
    if (!authToken || started.current) return;
    started.current = true;
    accept();
  }, [authToken, accept]);

  const hasToken = Boolean(getPendingJoinToken());

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5" component="h1">Join your department</Typography>

      {!authToken && hasToken && (
        <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body1">
            Sign in, or create an account, with the email address the invitation was sent to.
            You will join the department right after.
          </Typography>
          <Box>
            <Button variant="contained" onClick={onRequestSignIn}>Sign in or create account</Button>
          </Box>
        </Paper>
      )}

      {(state.status === 'missing' || (!authToken && !hasToken)) && (
        <Alert severity="info">
          No invitation found. Open the link from your invitation email again.
        </Alert>
      )}

      {state.status === 'joining' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Joining…</Typography>
        </Box>
      )}

      {state.status === 'joined' && (
        <Alert severity="success" action={<Button color="inherit" size="small" onClick={onDone}>Start chatting</Button>}>
          You have joined your department. Guardian now uses your department&apos;s plan.
        </Alert>
      )}

      {state.status === 'refused' && <Alert severity="warning">{state.message}</Alert>}

      {state.status === 'retry' && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={accept}>Retry</Button>}>
          {state.message}
        </Alert>
      )}
    </Box>
  );
}
